import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import API from '../../config/axiosConfig';
import { useFile } from '../../services/FileService';
import RecentProtocolsManager from './RecentProtocolsManager';
import Loader from '../../components/Loader';
import SimplePDFViewer from '../../components/SimplePDFViewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faDownload, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './ProtocolViewer.scss';
import ProtocolsAiChat from './ProtocolsAiChat';

const ProtocolViewer = () => {
  const [protocol, setProtocol] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isSwitchingFiles, setIsSwitchingFiles] = useState(false);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { protocolAccessed } = RecentProtocolsManager();
  
  // Get the current file to display
  const currentFile = protocol?.files?.[currentFileIndex];
  const { signedUrl, loading: urlLoading, error: urlError } = useFile(currentFile?.url);

  useEffect(() => {
    if (id) {
      protocolAccessed(id);
    }
  }, [id, protocolAccessed]);

  useEffect(() => {
    const fetchProtocol = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await API.get(`/api/protocols/view/${id}`);
        setProtocol(response.data);
        
        // Check if there's a specific file index in URL params
        const fileIndex = searchParams.get('fileIndex');
        if (fileIndex && !isNaN(parseInt(fileIndex))) {
          const index = parseInt(fileIndex);
          if (index >= 0 && index < response.data.files?.length) {
            setCurrentFileIndex(index);
          }
        }
      } catch (err) {
        console.error('Error fetching protocol:', err);
        setError(err.response?.data?.message || 'Failed to load protocol');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchProtocol();
    }
  }, [id, searchParams]);

  useEffect(() => {
    if (urlError) {
      setError('Failed to load file. Please try again.');
    }
  }, [urlError]);

  useEffect(() => {
    // When the current file changes, we're no longer switching
    if (currentFile) {
      setIsSwitchingFiles(false);
    }
  }, [currentFile]);

  const handleDownload = async () => {
    if (!currentFile || !signedUrl) {
      setError('No file available for download');
      return;
    }

    try {
      const response = await fetch(signedUrl);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentFile.name || `protocol-${id}-file-${currentFileIndex}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  // Handle file navigation
  const handleNextFile = () => {
    setIsSwitchingFiles(true);
    setCurrentFileIndex(prevIndex => (prevIndex + 1) % protocol.files.length);
  };

  const handleNavigateToDetails = () => {
    navigate(`/protocols/${id}`);
  };

  const renderFileViewer = () => {
    if (!currentFile) return null;

    const fileExtension = currentFile.relativeUrl?.split('.').pop()?.toLowerCase();
    const isPdf = fileExtension === 'pdf';
    const isImage = /\.(jpg|jpeg|png|gif|bmp)$/i.test(currentFile.relativeUrl);

    if (isPdf) {
      return (
        <SimplePDFViewer
          file={signedUrl}
          onError={(error) => {
            console.error('Error loading PDF:', error);
            setError('Failed to load PDF document');
          }}
        />
      );
    } else if (isImage) {
      return (
        <div className="image-viewer">
          <img 
            src={signedUrl} 
            alt={currentFile.name}
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            onError={() => setError('Failed to load image')}
          />
        </div>
      );
    } else {
      return (
        <div className="file-viewer-placeholder">
          <p>תצוגה מקדימה אינה זמינה עבור סוג קובץ זה</p>
          <p>שם הקובץ: {currentFile.name}</p>
          <button onClick={handleDownload}>הורד קובץ</button>
        </div>
      );
    }
  };

  if (isLoading || urlLoading || isSwitchingFiles) {
    return <Loader isLoading={true} />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!protocol) {
    return (
      <div className="error-container">
        <p>Protocol not found</p>
      </div>
    );
  }

  if (!protocol.files || protocol.files.length === 0) {
    return (
      <div className="error-container">
        <p>אין קבצים זמינים לפרוטוקול זה</p>
      </div>
    );
  }

  return (
    <div className="protocol-viewer-container">      
      <div className="file-navigation">
        <div className="file-nav-controls">
          <div className="file-nav-actions-right">
            <button onClick={handleDownload} className="nav-button">
              <FontAwesomeIcon icon={faDownload} /> הורד
            </button>
            <button onClick={handleNavigateToDetails} className="nav-button">
              <FontAwesomeIcon icon={faInfoCircle} /> פרטים
            </button>
            {protocol.files.length > 1 && (
              <button onClick={handleNextFile} className="nav-button">
                קובץ הבא <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            )}
          </div>
          <div className="file-nav-info">
            <span>{protocol.name}</span>
            <span> | </span>
            <span>{protocol.files.length} קבצים מצורפים</span>
          </div>
        </div>
      </div>

      <div className="protocol-viewer">
        {renderFileViewer()}
      </div>
      <ProtocolsAiChat hasProtocols={!!protocol} />
    </div>
  );
};

export default ProtocolViewer;
